import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react"
import { LuLoaderCircle, LuPlus, LuTrash2 } from "react-icons/lu"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { toast } from "sonner"
import { v4 as uuidv4 } from "uuid"

import { getLearningCues, setLearningCues } from "~/v2/api/vaas"
import Button from "~/v2/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/v2/components/ui/card"
import type {
  EditableLearningCue,
  LearningCueEditorState,
  RawLearningCue,
  VaasCueType,
  VaasId,
  VaasLesson,
  VaasOption,
  VaasQuestion,
} from "~/v2/types/vaas"
import { openV2ErrorToast } from "~/v2/utils/toast"

type VaasLearningCuesSectionHandle = {
  reset: () => void
  save: () => Promise<void>
}

type VaasLearningCuesSectionProps = {
  discardSignal?: number
  isPublished?: boolean
  lesson: VaasLesson
  onDirtyChange?: (isDirty: boolean) => void
}

type QuestionEditorProps = {
  cueType: VaasCueType
  disabled?: boolean
  onAddOption: () => void
  onChangeCorrectOption: (optionId: string) => void
  onChangeOption: (optionId: string, value: string) => void
  onChangeQuestion: (patch: Partial<VaasQuestion>) => void
  onRemoveOption: (optionId: string) => void
  question: VaasQuestion
}

const CUE_TYPES: Array<{ label: string; value: VaasCueType }> = [
  { label: "Knowledge Check", value: "knowledgeCheck" },
  { label: "Sentiment Questions", value: "sentimentQuestions" },
]

const newOption = (): VaasOption => ({
  id: uuidv4(),
  name: "",
})

const newKnowledgeQuestion = (): VaasQuestion => {
  const firstOption = newOption()
  const secondOption = newOption()

  return {
    attempts: 1,
    explanation: "",
    id: uuidv4(),
    image: "",
    image_title: "",
    name: "",
    sets: [
      {
        correct_option_id: firstOption.id,
        id: uuidv4(),
        options: [firstOption, secondOption],
      },
    ],
    type: "multiple_choice",
  }
}

const newSentimentQuestion = (): VaasQuestion => ({
  id: uuidv4(),
  name: "",
  sets: [
    {
      id: uuidv4(),
      options: [newOption(), newOption()],
    },
  ],
  type: "multiple_choice",
})

const sortBySeconds = <T extends { start_seconds?: number | string }>(
  cues: T[],
) =>
  [...cues].sort(
    (left, right) => Number(left.start_seconds) - Number(right.start_seconds),
  )

const hasUnsupportedCueType = (cue: RawLearningCue) =>
  Boolean(
    cue?.media ||
    cue?.topBanner ||
    cue?.bottomBanner ||
    cue?.fullscreen ||
    cue?.readyCheck ||
    cue?.prefilledNotes,
  )

const hasUnsupportedQuestionType = (questions: VaasQuestion[]) =>
  questions.some((question) => question?.type !== "multiple_choice")

const getSupportedCueType = (cue: RawLearningCue): VaasCueType | null => {
  if (hasUnsupportedCueType(cue)) {
    return null
  }

  if (cue?.knowledgeCheck?.questions?.length) {
    return hasUnsupportedQuestionType(cue.knowledgeCheck.questions)
      ? null
      : "knowledgeCheck"
  }

  if (cue?.sentimentQuestions?.length) {
    return hasUnsupportedQuestionType(cue.sentimentQuestions)
      ? null
      : "sentimentQuestions"
  }

  return null
}

const toEditableCue = (cue: RawLearningCue): EditableLearningCue | null => {
  const type = getSupportedCueType(cue)

  if (!type) {
    return null
  }

  return {
    id: cue.id,
    questions: [],
    sourceCue: cue,
    start_seconds: cue.start_seconds ?? 0,
    type,
  }
}

const buildCuePayload = (editableCue: EditableLearningCue): RawLearningCue => {
  const baseCue = {
    ...(editableCue.sourceCue || {}),
    force_pause: true,
    fullscreen: false,
    id: editableCue.id,
    start_seconds: Number(editableCue.start_seconds),
  }

  if (editableCue.type === "knowledgeCheck") {
    delete baseCue.sentimentQuestions
    return {
      ...baseCue,
      knowledgeCheck: {
        id: editableCue.sourceCue?.knowledgeCheck?.id || uuidv4(),
        questions: editableCue.questions,
      },
    }
  }

  delete baseCue.knowledgeCheck
  return {
    ...baseCue,
    sentimentQuestions: editableCue.questions,
  }
}

const buildDefaultCue = (
  type: VaasCueType = "knowledgeCheck",
): EditableLearningCue => ({
  id: uuidv4(),
  sourceCue: null,
  start_seconds: 0,
  type,
  questions: [
    type === "knowledgeCheck" ? newKnowledgeQuestion() : newSentimentQuestion(),
  ],
})

const normalizeEditableCue = (cue: RawLearningCue) => {
  const editableCue = toEditableCue(cue)
  if (!editableCue) {
    return null
  }

  if (editableCue.type === "knowledgeCheck") {
    editableCue.questions = (cue.knowledgeCheck?.questions || []).map(
      (question) => ({
        ...question,
        sets: (question.sets || []).map((set) => ({
          ...set,
          options: set.options || [],
        })),
      }),
    )
  } else {
    editableCue.questions = (cue.sentimentQuestions || []).map((question) => ({
      ...question,
      sets: (question.sets || []).map((set) => ({
        ...set,
        options: set.options || [],
      })),
    }))
  }

  return editableCue
}

const buildEditorState = (cues: RawLearningCue[]): LearningCueEditorState => {
  const supported: EditableLearningCue[] = []
  const unsupported: RawLearningCue[] = []

  ;(cues || []).forEach((cue) => {
    const editableCue = normalizeEditableCue(cue)
    if (editableCue) {
      supported.push(editableCue)
    } else {
      unsupported.push(cue)
    }
  })

  return {
    supported: sortBySeconds(supported),
    unsupported: sortBySeconds(unsupported),
  }
}

const summarizeUnsupportedCue = (cue: RawLearningCue) => {
  if (cue.media) return "Media cue"
  if (cue.topBanner) return "Top banner cue"
  if (cue.bottomBanner) return "Bottom banner cue"
  if (cue.fullscreen) return "Fullscreen cue"
  if (cue.readyCheck) return "Ready check"
  if (cue.prefilledNotes) return "Notes cue"
  if (cue.knowledgeCheck)
    return "Knowledge check with unsupported question types"
  if (cue.sentimentQuestions)
    return "Sentiment cue with unsupported question types"
  return "Unsupported cue"
}

const validateCue = (cue: EditableLearningCue, duration: number | null) => {
  const numericSeconds = Number(cue.start_seconds)
  if (Number.isNaN(numericSeconds) || numericSeconds < 0) {
    return "Cue seconds must be 0 or greater."
  }

  if (duration && numericSeconds > duration) {
    return "Cue seconds cannot be greater than lesson duration."
  }

  if (!cue.questions?.length) {
    return "Each cue needs at least one question."
  }

  for (const question of cue.questions) {
    if (!question.name?.trim()) {
      return "Each question needs prompt text."
    }

    const options = question.sets?.[0]?.options || []
    if (options.length < 2) {
      return "Each question needs at least two options."
    }

    if (options.some((option) => !option.name?.trim())) {
      return "Each option needs text."
    }

    if (
      cue.type === "knowledgeCheck" &&
      !question.sets?.[0]?.correct_option_id
    ) {
      return "Each knowledge check question needs a correct answer."
    }
  }

  return null
}

const QuestionEditor = ({
  disabled = false,
  cueType,
  onAddOption,
  onChangeCorrectOption,
  onChangeQuestion,
  onChangeOption,
  onRemoveOption,
  question,
}: QuestionEditorProps) => {
  const options = question.sets?.[0]?.options || []

  return (
    <div className="rounded-md border border-border bg-muted/20 p-4">
      <div className="grid gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-foreground">Prompt</span>
          <textarea
            className="min-h-[88px] rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary"
            disabled={disabled}
            onChange={(event) => onChangeQuestion({ name: event.target.value })}
            value={question.name || ""}
          />
        </label>

        {cueType === "knowledgeCheck" && (
          <label className="flex flex-col gap-2">
            <span className="text-sm font-medium text-foreground">
              Explanation
            </span>
            <textarea
              className="min-h-[72px] rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none transition-colors focus:border-primary"
              disabled={disabled}
              onChange={(event) =>
                onChangeQuestion({ explanation: event.target.value })
              }
              value={question.explanation || ""}
            />
          </label>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-foreground">Options</div>
            <Button
              disabled={disabled}
              onClick={onAddOption}
              size="sm"
              type="button"
              variant="outline"
            >
              <LuPlus className="mr-2 h-4 w-4" />
              Add option
            </Button>
          </div>

          {options.map((option) => (
            <div
              className="grid gap-3 md:grid-cols-[minmax(0,1fr)_140px_auto]"
              key={option.id}
            >
              <input
                className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
                disabled={disabled}
                onChange={(event) =>
                  onChangeOption(option.id, event.target.value)
                }
                placeholder="Option text"
                value={option.name || ""}
              />
              {cueType === "knowledgeCheck" ? (
                <label className="flex h-10 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm text-foreground">
                  <input
                    checked={
                      question.sets?.[0]?.correct_option_id === option.id
                    }
                    disabled={disabled}
                    onChange={() => onChangeCorrectOption(option.id)}
                    type="radio"
                  />
                  Correct
                </label>
              ) : (
                <div className="h-10 rounded-md border border-border bg-background px-3 text-sm text-muted-foreground flex items-center">
                  Response option
                </div>
              )}
              <Button
                disabled={disabled || options.length <= 2}
                onClick={() => onRemoveOption(option.id)}
                size="sm"
                type="button"
                variant="ghost"
              >
                <LuTrash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const VaasLearningCuesSection = forwardRef<
  VaasLearningCuesSectionHandle,
  VaasLearningCuesSectionProps
>(({ discardSignal = 0, isPublished = false, lesson, onDirtyChange }, ref) => {
  const queryClient = useQueryClient()
  const [supportedCues, setSupportedCues] = useState<EditableLearningCue[]>([])
  const [unsupportedCues, setUnsupportedCues] = useState<RawLearningCue[]>([])
  const [saveError, setSaveError] = useState("")
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState("")
  const [lastSavedState, setLastSavedState] = useState({
    supported: [] as EditableLearningCue[],
    unsupported: [] as RawLearningCue[],
  })
  const hasVideo = Boolean(lesson?.video_id)
  const videoDuration = Number.isFinite(Number(lesson?.duration))
    ? Math.floor(Number(lesson.duration))
    : null

  const learningCuesQuery = useQuery(
    ["vaas-learning-cues", lesson.id],
    () => getLearningCues(lesson.id),
    {
      cacheTime: 0,
      onSuccess: (data) => {
        const nextState = buildEditorState(data)
        setSupportedCues(nextState.supported)
        setUnsupportedCues(nextState.unsupported)
        setLastSavedState(nextState)
        setLastSavedSnapshot(JSON.stringify(nextState))
        setSaveError("")
      },
    },
  )

  const saveMutation = useMutation(
    ({ data, lessonId }: { data: RawLearningCue[]; lessonId: VaasId }) =>
      setLearningCues(data, lessonId),
    {
      onSuccess: (responseData) => {
        queryClient.setQueryData(
          ["vaas-learning-cues", lesson.id],
          responseData,
        )
        const nextState = buildEditorState(responseData)
        setSupportedCues(nextState.supported)
        setUnsupportedCues(nextState.unsupported)
        setLastSavedState(nextState)
        setLastSavedSnapshot(JSON.stringify(nextState))
        toast.success("Learning cues updated")
        setSaveError("")
      },
      onError: (err) => {
        const message = "There was an error saving learning cues"
        setSaveError(message)
        openV2ErrorToast(err, message)
      },
    },
  )

  useEffect(() => {
    if (learningCuesQuery.data) {
      const nextState = buildEditorState(learningCuesQuery.data)
      setSupportedCues(nextState.supported)
      setUnsupportedCues(nextState.unsupported)
      setLastSavedState(nextState)
      setLastSavedSnapshot(JSON.stringify(nextState))
    }
  }, [learningCuesQuery.data])

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        supported: supportedCues,
        unsupported: unsupportedCues,
      }),
    [supportedCues, unsupportedCues],
  )
  const isDirty = currentSnapshot !== lastSavedSnapshot

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    setSupportedCues(lastSavedState.supported || [])
    setUnsupportedCues(lastSavedState.unsupported || [])
    setSaveError("")
  }, [discardSignal, lastSavedState])

  const updateCue = (
    cueId: string,
    updater: (cue: EditableLearningCue) => EditableLearningCue,
  ) => {
    setSupportedCues((currentCues) =>
      currentCues.map((cue) => (cue.id === cueId ? updater(cue) : cue)),
    )
  }

  const addCue = () => {
    if (!hasVideo || isPublished) {
      return
    }
    setSupportedCues((currentCues) =>
      sortBySeconds([...currentCues, buildDefaultCue()]),
    )
  }

  const removeCue = (cueId: string) => {
    setSupportedCues((currentCues) =>
      currentCues.filter((cue) => cue.id !== cueId),
    )
  }

  const handleSave = useCallback(async () => {
    if (isPublished) {
      const message =
        "VAAS courses that have been published cannot update learning cues."
      setSaveError(message)
      toast.error(message)
      throw new Error(message)
    }

    if (!hasVideo) {
      const message = "Add a video before creating learning cues."
      setSaveError(message)
      toast.error(message)
      throw new Error(message)
    }

    setSaveError("")

    for (const cue of supportedCues) {
      const validationError = validateCue(cue, lesson?.duration || null)
      if (validationError) {
        setSaveError(validationError)
        toast.error(validationError)
        throw new Error(validationError)
      }
    }

    const mergedPayload = sortBySeconds([
      ...unsupportedCues,
      ...supportedCues.map(buildCuePayload),
    ])

    await saveMutation.mutateAsync({
      data: mergedPayload,
      lessonId: lesson.id,
    })
  }, [
    hasVideo,
    isPublished,
    lesson.duration,
    lesson.id,
    saveMutation,
    supportedCues,
    unsupportedCues,
  ])

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        setSupportedCues(lastSavedState.supported || [])
        setUnsupportedCues(lastSavedState.unsupported || [])
        setSaveError("")
      },
      save: handleSave,
    }),
    [handleSave, lastSavedState],
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Learning cues</CardTitle>
        <CardDescription>
          Basic VAAS cue editor for knowledge checks and sentiment questions.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {learningCuesQuery.isLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <LuLoaderCircle className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : null}

        {learningCuesQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Failed to load learning cues. Refresh and try again.
          </div>
        ) : null}

        {!learningCuesQuery.isLoading &&
        !learningCuesQuery.isError &&
        hasVideo &&
        videoDuration !== null ? (
          <div className="rounded-md border border-border bg-muted/20 px-4 py-3 text-sm text-foreground">
            Video duration: {videoDuration}s
          </div>
        ) : null}

        {!learningCuesQuery.isLoading &&
        !learningCuesQuery.isError &&
        unsupportedCues.length > 0 ? (
          <div className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
            <div className="font-medium">
              Some legacy cues are read-only in VAAS.
            </div>
            <div className="mt-1">
              {unsupportedCues.length} unsupported cue
              {unsupportedCues.length === 1 ? "" : "s"} will be preserved
              unchanged.
            </div>
            <ul className="mt-2 list-disc pl-5">
              {unsupportedCues.map((cue) => (
                <li key={cue.id}>
                  {summarizeUnsupportedCue(cue)} at {cue.start_seconds}s
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!learningCuesQuery.isLoading &&
        !learningCuesQuery.isError &&
        isPublished ? (
          <div className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
            VAAS courses that have been published cannot create or update
            learning cues.
          </div>
        ) : null}

        {!learningCuesQuery.isLoading &&
        !learningCuesQuery.isError &&
        supportedCues.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
            {isPublished
              ? "Learning cues are locked after this VAAS course has been published."
              : hasVideo
                ? "No editable VAAS learning cues yet."
                : "Add a video before creating learning cues."}
          </div>
        ) : null}

        {!learningCuesQuery.isLoading &&
        !learningCuesQuery.isError &&
        !hasVideo ? (
          <div className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-foreground">
            Add a video before creating or editing learning cues in VAAS.
          </div>
        ) : null}

        {!learningCuesQuery.isLoading &&
          !learningCuesQuery.isError &&
          supportedCues.map((cue) => (
            <div
              className="rounded-lg border border-border bg-background"
              key={cue.id}
            >
              <div className="flex flex-col gap-4 border-b border-border p-4 md:flex-row md:items-start md:justify-between">
                <div className="grid flex-1 gap-4 md:grid-cols-[140px_220px]">
                  <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-foreground">
                      Seconds
                    </span>
                    <input
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
                      disabled={!hasVideo || isPublished}
                      min="0"
                      onChange={(event) =>
                        updateCue(cue.id, (currentCue) => ({
                          ...currentCue,
                          start_seconds: event.target.value,
                        }))
                      }
                      step="1"
                      type="number"
                      value={cue.start_seconds}
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-foreground">
                      Cue type
                    </span>
                    <select
                      className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
                      disabled={!hasVideo || isPublished}
                      onChange={(event) =>
                        updateCue(cue.id, (currentCue) => ({
                          ...currentCue,
                          questions: [
                            event.target.value === "knowledgeCheck"
                              ? newKnowledgeQuestion()
                              : newSentimentQuestion(),
                          ],
                          type: event.target.value as VaasCueType,
                        }))
                      }
                      value={cue.type}
                    >
                      {CUE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <Button
                  disabled={!hasVideo || isPublished}
                  onClick={() => removeCue(cue.id)}
                  type="button"
                  variant="ghost"
                >
                  <LuTrash2 className="mr-2 h-4 w-4" />
                  Remove cue
                </Button>
              </div>

              <div className="flex flex-col gap-4 p-4">
                {cue.questions.map((question, questionIndex) => (
                  <div className="flex flex-col gap-3" key={question.id}>
                    <div className="text-sm font-medium text-foreground">
                      Question {questionIndex + 1}
                    </div>
                    <QuestionEditor
                      disabled={!hasVideo || isPublished}
                      cueType={cue.type}
                      onAddOption={() =>
                        hasVideo &&
                        !isPublished &&
                        updateCue(cue.id, (currentCue) => {
                          const nextQuestions = [...currentCue.questions]
                          const nextQuestion = {
                            ...nextQuestions[questionIndex],
                          }
                          const nextSet = {
                            ...nextQuestion.sets[0],
                            options: [
                              ...nextQuestion.sets[0].options,
                              newOption(),
                            ],
                          }
                          nextQuestion.sets = [nextSet]
                          nextQuestions[questionIndex] = nextQuestion
                          return { ...currentCue, questions: nextQuestions }
                        })
                      }
                      onChangeCorrectOption={(optionId) =>
                        hasVideo &&
                        !isPublished &&
                        updateCue(cue.id, (currentCue) => {
                          const nextQuestions = [...currentCue.questions]
                          const nextQuestion = {
                            ...nextQuestions[questionIndex],
                          }
                          nextQuestion.sets = [
                            {
                              ...nextQuestion.sets[0],
                              correct_option_id: optionId,
                            },
                          ]
                          nextQuestions[questionIndex] = nextQuestion
                          return { ...currentCue, questions: nextQuestions }
                        })
                      }
                      onChangeOption={(optionId, value) =>
                        hasVideo &&
                        !isPublished &&
                        updateCue(cue.id, (currentCue) => {
                          const nextQuestions = [...currentCue.questions]
                          const nextQuestion = {
                            ...nextQuestions[questionIndex],
                          }
                          nextQuestion.sets = [
                            {
                              ...nextQuestion.sets[0],
                              options: nextQuestion.sets[0].options.map(
                                (option) =>
                                  option.id === optionId
                                    ? { ...option, name: value }
                                    : option,
                              ),
                            },
                          ]
                          nextQuestions[questionIndex] = nextQuestion
                          return { ...currentCue, questions: nextQuestions }
                        })
                      }
                      onChangeQuestion={(patch) =>
                        hasVideo &&
                        !isPublished &&
                        updateCue(cue.id, (currentCue) => {
                          const nextQuestions = [...currentCue.questions]
                          nextQuestions[questionIndex] = {
                            ...nextQuestions[questionIndex],
                            ...patch,
                          }
                          return { ...currentCue, questions: nextQuestions }
                        })
                      }
                      onRemoveOption={(optionId) =>
                        hasVideo &&
                        !isPublished &&
                        updateCue(cue.id, (currentCue) => {
                          const nextQuestions = [...currentCue.questions]
                          const nextQuestion = {
                            ...nextQuestions[questionIndex],
                          }
                          const nextOptions =
                            nextQuestion.sets[0].options.filter(
                              (option) => option.id !== optionId,
                            )
                          let correctOptionId =
                            nextQuestion.sets[0].correct_option_id
                          if (correctOptionId === optionId) {
                            correctOptionId = nextOptions[0]?.id || ""
                          }
                          nextQuestion.sets = [
                            {
                              ...nextQuestion.sets[0],
                              correct_option_id: correctOptionId,
                              options: nextOptions,
                            },
                          ]
                          nextQuestions[questionIndex] = nextQuestion
                          return { ...currentCue, questions: nextQuestions }
                        })
                      }
                      question={question}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

        {saveError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {saveError}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            disabled={!hasVideo || isPublished}
            onClick={addCue}
            type="button"
            variant="outline"
          >
            <LuPlus className="mr-2 h-4 w-4" />
            Add cue
          </Button>
          <Button
            disabled={
              !hasVideo ||
              !isDirty ||
              isPublished ||
              saveMutation.isLoading ||
              learningCuesQuery.isLoading
            }
            onClick={handleSave}
            type="button"
          >
            {saveMutation.isLoading ? "Saving cues..." : "Save cues"}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
})

VaasLearningCuesSection.displayName = "VaasLearningCuesSection"

export default VaasLearningCuesSection
